import React, { useMemo, useRef, useState } from 'react';
import { DragDropContext, Draggable } from 'react-beautiful-dnd';
import {
    ADD_ORDER_LABEL,
    BOARD_MODES,
    CLAIM_DRAG_TYPE,
    EMPTY_CLAIMS_HINT,
    EMPTY_COLUMN_HINT,
    EMPTY_SERVICES_HINT,
    ITEM_DRAG_TYPE,
    NO_ITEMS_SUMMARY,
    SERVICE_DRAG_TYPE,
    TRAY_DROPPABLE_ID,
} from '../constants';
import { useFocusAfterUpdate } from '../useFocusAfterUpdate';
import {
    MIN_NEW_ORDERS,
    canRemoveOrder,
    childLabel,
    claimDetailRows,
    claimName,
    columnProblems,
    columnSummary,
    dividedItemIds,
    itemDetailRows,
    itemFullName,
    moveFromDrop,
    moveTargets,
    serviceDetailRows,
    serviceLines,
} from '../utils';
import { BillableSwitch } from './BillableSwitch';
import { AddOrderButton, BoardRow, ColumnActions, DragItem, ItemSummary, ItemSummaryCount } from './blocks';
import { CardDetailsDialog } from './CardDetailsDialog';
import { ClaimCard } from './ClaimCard';
import { ItemCard } from './ItemCard';
import { MoveToMenu } from './MoveToMenu';
import { OrderColumn } from './OrderColumn';
import { RemoveOrderButton } from './RemoveOrderButton';
import { ServiceCard } from './ServiceCard';
import { UnassignedTray } from './UnassignedTray';

/**
 * The split board: the Unassigned tray, then one column per new order, each showing the cards the layout puts
 * there for the current mode. In items mode those are item cards; in services mode they are the parent's services,
 * and in claims mode its open claims; in those two modes each column shows its item lines as a collapsed one-line
 * summary. Cards move by drag and drop (mouse or keyboard) or by each card's Move to menu. Item, service, and claim
 * cards use separate drag types, so none can land in another kind's list. Item moves are reported through `onMove`
 * (apply with `moveLine`; return false when the move waits on a prompt, so the board leaves focus to the caller),
 * service moves through `onAssignService` (apply with `assignService`), and claim moves through `onAssignClaim`
 * (apply with `assignClaim`); the caller owns the layout. "+ Add order" and each column's remove
 * control (shown only above the minimum column count) call `onAddOrder` and `onRemoveOrder`; apply them with
 * `addOrder` and `removeOrder`. Each column header has a Billable switch when `onBillableChange` is given; apply it
 * with `setOrderBillable`. A column that is off says it isn't billable under its header. In items mode a column
 * with a problem (`columnProblems`) shows it at its foot. Hovering a card's name shows it in full (not during a
 * drag), and each card's info button opens a read-only details dialog.
 *
 * @param {{ layout: import('../utils').SplitLayout, items: object[], services?: object[], claims?: object[], mode?: 'items'|'services'|'claims', parentOrderNumber: string, onMove: (move: import('../utils').LineMove) => boolean|void, onAssignService?: (serviceId: string, orderKey: string|null) => void, onAssignClaim?: (claimId: string, orderKey: string|null) => void, onAddOrder?: () => void, onRemoveOrder?: (orderKey: string) => void, onBillableChange?: (orderKey: string, billable: boolean) => void, disabled?: boolean, renderColumnActions?: (order: import('../utils').LayoutOrder, index: number) => React.ReactNode }} props
 *   `items` are the parent's item lines, `services` its services, and `claims` its open claims, from the order read, in order. `mode` is a
 *   `BOARD_MODES` value (items by default). `disabled` locks dragging, the menus, and adding or removing columns, for
 *   example while a split is submitting; it also locks the Billable switches. `renderColumnActions` adds controls to
 *   the right side of each column header, after the Billable switch and before the remove control.
 */
export const SplitBoard = ({
    layout: rawLayout,
    items,
    services = [],
    claims = [],
    mode = BOARD_MODES.ITEMS,
    parentOrderNumber,
    onMove,
    onAssignService,
    onAssignClaim,
    onAddOrder,
    onRemoveOrder,
    onBillableChange,
    disabled = false,
    renderColumnActions,
}) => {
    const lineInfo = useMemo(
        () => new Map((items || []).map((item, index) => [item.item_id, { item, lineNumber: index + 1 }])),
        [items]
    );
    const serviceIds = useMemo(() => new Set((services || []).map((service) => service.service_id)), [services]);
    const claimIds = useMemo(() => new Set((claims || []).map((claim) => claim.claim_id)), [claims]);
    // Show and count only cards whose line, service, or claim is in the order read, and services and claims placed on
    // a column still on the board, so headers match the cards on screen.
    const layout = useMemo(() => {
        const orderKeys = new Set(rawLayout.orders.map((order) => order.key));
        const known = (map, ids) =>
            Object.fromEntries(
                Object.entries(map || {}).filter(([id, orderKey]) => ids.has(id) && orderKeys.has(orderKey))
            );
        return {
            ...rawLayout,
            lines: rawLayout.lines.filter((line) => lineInfo.has(line.item_id)),
            serviceMap: known(rawLayout.serviceMap, serviceIds),
            claimMap: known(rawLayout.claimMap, claimIds),
        };
    }, [rawLayout, lineInfo, serviceIds, claimIds]);
    const divided = useMemo(() => dividedItemIds(layout), [layout]);
    const targets = useMemo(() => moveTargets(layout, parentOrderNumber), [layout, parentOrderNumber]);
    const boardRef = useRef(null);
    const addOrderRef = useRef(null);
    const focusLater = useFocusAfterUpdate(rawLayout);
    // Name tooltips stay shut while a card is being dragged.
    const [dragActive, setDragActive] = useState(false);
    const [details, setDetails] = useState(null);

    // Cards moved from their menu remount in the new list, so find the handle again after the move.
    const findHandle = (portionKey) => () =>
        [...(boardRef.current?.querySelectorAll('[data-rbd-drag-handle-draggable-id]') || [])].find(
            (node) => node.getAttribute('data-rbd-drag-handle-draggable-id') === portionKey
        );

    // After a column is removed, focus the remove control now at its place (or the one before), or "+ Add order"
    // when no column can be removed any more.
    const handleRemove = (orderKey, index) => {
        const remaining = layout.orders.length - 1;
        focusLater(() => {
            if (remaining > MIN_NEW_ORDERS) {
                const label = childLabel(parentOrderNumber, Math.min(index, remaining - 1));
                const button = boardRef.current?.querySelector(`button[aria-label="Remove ${label}"]`);
                if (button) return button;
            }
            return addOrderRef.current;
        });
        onRemoveOrder(orderKey);
    };

    const removable = canRemoveOrder(layout);

    const servicesMode = mode === BOARD_MODES.SERVICES;
    const claimsMode = mode === BOARD_MODES.CLAIMS;
    // Services and claims both go whole to one order, so their modes share the same column layout.
    const wholeMode = servicesMode || claimsMode;
    const problems = useMemo(() => columnProblems(layout, items), [layout, items]);
    const serviceList = services || [];
    const claimList = claims || [];

    const handleDragEnd = (result) => {
        setDragActive(false);
        const whole = {
            [SERVICE_DRAG_TYPE]: { map: layout.serviceMap, assign: onAssignService },
            [CLAIM_DRAG_TYPE]: { map: layout.claimMap, assign: onAssignClaim },
        }[result?.type];
        if (whole) {
            const { draggableId, destination } = result;
            if (!destination) return;
            const toOrderKey = destination.droppableId === TRAY_DROPPABLE_ID ? null : destination.droppableId;
            if ((whole.map?.[draggableId] ?? null) !== toOrderKey) whole.assign?.(draggableId, toOrderKey);
            return;
        }
        const move = moveFromDrop(layout, result, TRAY_DROPPABLE_ID);
        if (move) onMove(move);
    };

    // Cards for rows that always move whole (services and claims): one Draggable per row placed at `orderKey`.
    const wholeCardsFor = (orderKey, { list, map, idOf, nameOf, detailRowsOf, onAssign, renderCard }) =>
        list
            .filter((row) => (map?.[idOf(row)] ?? null) === orderKey)
            .map((row, index) => {
                const id = idOf(row);
                const cardName = nameOf(row);
                return (
                    <Draggable key={id} draggableId={id} index={index} isDragDisabled={disabled}>
                        {(provided, snapshot) => (
                            <DragItem
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                aria-label={cardName}
                                dragging={snapshot.isDragging}
                            >
                                {renderCard(row, {
                                    tooltipSuppressed: dragActive,
                                    onShowDetails: () => setDetails({ title: cardName, rows: detailRowsOf(row) }),
                                    actions: (
                                        <MoveToMenu
                                            targets={targets}
                                            currentKey={orderKey}
                                            disabled={disabled}
                                            cardName={cardName}
                                            onMove={(toOrderKey) => {
                                                focusLater(findHandle(id));
                                                onAssign?.(id, toOrderKey);
                                            }}
                                        />
                                    ),
                                })}
                            </DragItem>
                        )}
                    </Draggable>
                );
            });

    const serviceCardsFor = (orderKey) =>
        wholeCardsFor(orderKey, {
            list: serviceList,
            map: layout.serviceMap,
            idOf: (service) => service.service_id,
            nameOf: (service) => serviceLines([service])[0],
            detailRowsOf: serviceDetailRows,
            onAssign: onAssignService,
            renderCard: (service, props) => <ServiceCard service={service} {...props} />,
        });

    const claimCardsFor = (orderKey) =>
        wholeCardsFor(orderKey, {
            list: claimList,
            map: layout.claimMap,
            idOf: (claim) => claim.claim_id,
            nameOf: claimName,
            detailRowsOf: claimDetailRows,
            onAssign: onAssignClaim,
            renderCard: (claim, props) => <ClaimCard claim={claim} {...props} />,
        });

    const modeCardsFor = (orderKey) => {
        if (servicesMode) return serviceCardsFor(orderKey);
        if (claimsMode) return claimCardsFor(orderKey);
        return cardsFor(orderKey);
    };

    /** The current mode's column header count, empty flag, drag type, and drop hint. */
    const columnMode = (summary) => {
        if (servicesMode) {
            return {
                meta: summary.serviceText,
                empty: summary.serviceCount === 0,
                type: SERVICE_DRAG_TYPE,
                hint: EMPTY_SERVICES_HINT,
            };
        }
        if (claimsMode) {
            return {
                meta: summary.claimText,
                empty: summary.claimCount === 0,
                type: CLAIM_DRAG_TYPE,
                hint: EMPTY_CLAIMS_HINT,
            };
        }
        return { meta: summary.text, empty: summary.lineCount === 0, type: ITEM_DRAG_TYPE, hint: EMPTY_COLUMN_HINT };
    };

    // Collapsed item lines for a column in services or claims mode, so the user can tell the orders apart.
    const itemSummary = (orderKey, summary) => {
        if (summary.lineCount === 0) return <ItemSummary>{NO_ITEMS_SUMMARY}</ItemSummary>;
        const names = [
            ...new Set(
                layout.lines
                    .filter((line) => line.orderKey === orderKey)
                    .map(({ item_id: id }) => lineInfo.get(id).item.sku || lineInfo.get(id).item.description)
                    .filter(Boolean)
            ),
        ];
        return (
            <ItemSummary data-testid="split-board-item-summary">
                <ItemSummaryCount>{summary.itemText}</ItemSummaryCount>
                {names.length ? ` · ${names.join(', ')}` : null}
            </ItemSummary>
        );
    };

    const cardsFor = (orderKey) =>
        layout.lines
            .filter((line) => (line.orderKey ?? null) === orderKey)
            .map((line, index) => {
                const { item, lineNumber } = lineInfo.get(line.item_id);
                const cardName = `Line ${lineNumber}, ${item.sku || item.description || 'item'}`;
                return (
                    <Draggable
                        key={line.portionKey}
                        draggableId={line.portionKey}
                        index={index}
                        isDragDisabled={disabled}
                    >
                        {(provided, snapshot) => (
                            <DragItem
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                aria-label={cardName}
                                dragging={snapshot.isDragging}
                            >
                                <ItemCard
                                    item={item}
                                    lineNumber={lineNumber}
                                    quantity={line.quantity}
                                    divided={divided.has(line.item_id)}
                                    tooltipSuppressed={dragActive}
                                    onShowDetails={() =>
                                        setDetails({
                                            title: `Line ${lineNumber} · ${itemFullName(item)}`,
                                            rows: itemDetailRows(item, { lineNumber, quantity: line.quantity }),
                                        })
                                    }
                                    actions={
                                        <MoveToMenu
                                            targets={targets}
                                            currentKey={orderKey}
                                            disabled={disabled}
                                            cardName={cardName}
                                            onMove={(toOrderKey) => {
                                                // false means the caller didn't move it yet (it asked how many),
                                                // so the caller places focus once it does.
                                                const moved = onMove({ portionKey: line.portionKey, toOrderKey });
                                                if (moved !== false) focusLater(findHandle(line.portionKey));
                                            }}
                                        />
                                    }
                                />
                            </DragItem>
                        )}
                    </Draggable>
                );
            });

    return (
        <DragDropContext onDragStart={() => setDragActive(true)} onDragEnd={handleDragEnd}>
            <BoardRow ref={boardRef} data-testid="split-board">
                <UnassignedTray
                    layout={layout}
                    mode={mode}
                    services={serviceList}
                    claims={claimList}
                    disabled={disabled}
                >
                    {modeCardsFor(null)}
                </UnassignedTray>
                {layout.orders.map((order, index) => {
                    const summary = columnSummary(layout, order.key);
                    const label = childLabel(parentOrderNumber, index);
                    return (
                        <OrderColumn
                            key={order.key}
                            orderKey={order.key}
                            label={label}
                            {...columnMode(summary)}
                            summary={wholeMode ? itemSummary(order.key, summary) : null}
                            notBillable={order.billable === false}
                            error={wholeMode ? null : problems[order.key] ?? null}
                            disabled={disabled}
                            actions={
                                <ColumnActions>
                                    {onBillableChange ? (
                                        <BillableSwitch
                                            label={label}
                                            checked={order.billable !== false}
                                            disabled={disabled}
                                            onChange={(billable) => onBillableChange(order.key, billable)}
                                        />
                                    ) : null}
                                    {renderColumnActions?.(order, index) ?? null}
                                    {onRemoveOrder && removable ? (
                                        <RemoveOrderButton
                                            label={label}
                                            disabled={disabled}
                                            onRemove={() => handleRemove(order.key, index)}
                                        />
                                    ) : null}
                                </ColumnActions>
                            }
                        >
                            {modeCardsFor(order.key)}
                        </OrderColumn>
                    );
                })}
                {onAddOrder ? (
                    <AddOrderButton ref={addOrderRef} type="button" disabled={disabled} onClick={onAddOrder}>
                        {ADD_ORDER_LABEL}
                    </AddOrderButton>
                ) : null}
            </BoardRow>
            <CardDetailsDialog details={details} onClose={() => setDetails(null)} />
        </DragDropContext>
    );
};

export default SplitBoard;
